import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { buildHttpParams } from '@/common/utils/http-params.util';
import { toLocalIsoDate } from '@/common/utils/to-local-iso-date.util';
import {
	CreateLedgerEntryFromScanPayload,
	CreateLedgerEntryFromScanResponse,
	Scan,
	ScanListFilters,
	ScanListResponse,
	SuggestedDriversResponse,
	UpdateScanPayload,
} from '@/tms/gbox-scans/_models/gbox-scans.model';

@Injectable({ providedIn: 'root' })
export class GboxScansApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = `${environment.erpSymfonyUrl}/api/v1/tms/gbox-scans`;

	list(page: number, limit: number, filters?: ScanListFilters): Observable<ScanListResponse> {
		const params = buildHttpParams({
			page,
			limit,
			from: filters?.from ? toLocalIsoDate(filters.from) : null,
			to: filters?.to ? toLocalIsoDate(filters.to) : null,
			status: filters?.status ?? null,
			costStatus: filters?.costStatus ?? null,
			categoryId: filters?.categoryId ?? null,
			ocrStatus: filters?.ocrStatus ?? null,
			boxId: filters?.boxId ?? null,
			sortBy: filters?.sortBy ?? null,
			sortDirection: filters?.sortDirection ?? null,
		});
		return this.http.get<ScanListResponse>(this.baseUrl, { params });
	}

	sync(): Observable<ScanListResponse> {
		return this.http.post<ScanListResponse>(`${this.baseUrl}/sync`, {});
	}

	syncRange(from: Date | null, to: Date | null): Observable<ScanListResponse> {
		const body = {
			from: from ? toLocalIsoDate(from) : null,
			to: to ? toLocalIsoDate(to) : null,
		};
		return this.http.post<ScanListResponse>(`${this.baseUrl}/sync-range`, body);
	}

	get(id: number): Observable<{ data: Scan }> {
		return this.http.get<{ data: Scan }>(`${this.baseUrl}/${id}`);
	}

	archive(id: number): Observable<{ data: Scan }> {
		return this.http.post<{ data: Scan }>(`${this.baseUrl}/${id}/archive`, {});
	}

	recomputeOcr(id: number): Observable<{ data: Scan }> {
		return this.http.post<{ data: Scan }>(`${this.baseUrl}/${id}/recompute-ocr`, {});
	}

	update(id: number, payload: UpdateScanPayload): Observable<{ data: Scan }> {
		return this.http.patch<{ data: Scan }>(`${this.baseUrl}/${id}`, payload);
	}

	createDriverLedgerEntry(
		id: number,
		payload: CreateLedgerEntryFromScanPayload,
	): Observable<CreateLedgerEntryFromScanResponse> {
		return this.http.post<CreateLedgerEntryFromScanResponse>(`${this.baseUrl}/${id}/driver-ledger-entry`, payload);
	}

	suggestedDrivers(id: number): Observable<SuggestedDriversResponse> {
		return this.http.get<SuggestedDriversResponse>(`${this.baseUrl}/${id}/suggested-drivers`);
	}
}
